import { Signal, ViewContainerRef, computed, effect, signal, untracked } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AgGridAngular } from 'ag-grid-angular';
import { AgPromise, CellKeyDownEvent, ColDef, Column, ColumnMovedEvent, ColumnState, DefaultMenuItem, GetContextMenuItemsParams, GetMainMenuItemsParams, GetRowIdFunc, GridApi, GridOptions, GridState, GridStateKey, IHeaderParams, IInnerHeaderComponent, MenuItemDef, RowGroupOpenedEvent, RowNode, RowSelectedEvent, SelectionChangedEvent, SortChangedEvent, StoreRefreshedEvent } from 'ag-grid-enterprise';
import { interval, tap, timer } from 'rxjs';
import { debounced } from 'src/app/pbir/utils/debounced';
import { logger } from 'src/app/pbir/utils/logger';
import { SortDirection } from 'src/app/services/api.services';
import { VisualHost } from '../../VisualHost';
import * as AgGridConstants from '../shared';
import { DataSource } from './dataSource';
import { PivotColumnHeader } from './headerComponent/ColumnHeader.component';
import { PivotColumnOrValueGroup, PivotGroup, PivotRow, PivotValue } from './pivot';
import { AgGridPivotTableVisualComponent } from './PivotTableVisual.component';
import { dataComparer } from './dataComparer';
import { VisualField } from '../../VisualField';
import { VisualFieldSorting } from '../../VisualFieldSorting';
const LOGGER = logger('AgGridPivotTableVisualComponent');
const ROWHEADER_COLID = 'ag-Grid-AutoColumn';

export class AgGridController {
  constructor(
    readonly component: AgGridPivotTableVisualComponent,
    readonly ag: Signal<AgGridAngular>,
    readonly viewContainerRef: ViewContainerRef
  ) { }

  readonly gridOptions = computed<GridOptions>(() => {
    let pivoted = this.component.columnFields().length > 0;

    return {
      ...AgGridConstants.data.ag.options,
      pivotMode: true,

      groupDisplayType: this.component.pivotColumnDisplay(),

      groupSuppressBlankHeader: true,
      removePivotHeaderRowWhenSingleValueColumn: false,
      suppressAggFuncInHeader: true,

      pivotGroupHeaderHeight: pivoted ? undefined : 0,

      hidePaddedHeaderRows: true,
      getRowHeight: this.getRowHeight,

      groupHideParentOfSingleChild: true,

      ssrmExpandAllAffectsAllRows: true,
      maxConcurrentDatasourceRequests: 16,

      serverSideEnableClientSideSort: true,

      cacheBlockSize: 9999,

      suppressMultiSort: true,

      processHeaderForClipboard: x => {
        if (x.column.getColId() == ROWHEADER_COLID) {
          return this.component.rowFields().map(x => x.shortTitle).join(' > ');
        }
        else {
          return (x.column.getUserProvidedColDef()?.headerValueGetter as Function)() ?? '';
        }
      },

      processGroupHeaderForClipboard: x => {
        return '';
      }
    };
  });

  readonly theme = computed(() => {
    let layout = this.component.tableLayout();

    return AgGridConstants.data.ag.theme.withParams({
      headerHeight: layout.headerHeightPx,
      rowHeight: layout.rowHeightPx,
      fontSize: layout.fontSize,
    });
  });

  readonly themeChanged = effect(() => {
    let theme = this.theme();
    untracked(() => {
      let grid = this.ag();
      if (grid?.api && !grid.api.isDestroyed()) {
        grid.api.resetRowHeights();
      }
    });
  });

  readonly getRowHeight = () => this.component.tableLayout().config.rowHeight;

  readonly rowGroupHeaderContainer = signal<HTMLDivElement>(null);

  readonly autoGroupColumnDef = computed<ColDef>(() => {
    let [rowFields, columnFields, pivotColumnDisplay] = [
      this.component.rowFields(),
      this.component.columnFields(),
      this.component.pivotColumnDisplay()];

    let component = this.component;
    let rowHeaderWidth = untracked(() => this.component.rowHeaderMinWidth());

    let sort;
    let sampleRow = rowFields[0]
    if (sampleRow?.sorted()) {
      sort = sampleRow.sortDirection() == SortDirection.Ascending ? 'asc' : 'desc';
    }

    return {
      headerValueGetter: params => params.colDef.headerName,

      minWidth: AgGridConstants.MIN_COLUMN_WIDTH,
      cellRendererParams: { suppressCount: false },

      sortable: true,
      sort: sort,
      suppressMovable: true,

      hide: true,
      pinned: untracked(() => !this.component.state()?.unfreezeRows()),

      width: rowHeaderWidth,

      headerComponentParams: {
        innerHeaderComponentParams: {
          component
        },

        innerHeaderComponent: class implements IInnerHeaderComponent {
          headersContainer: HTMLDivElement;
          rowHeaderTitleSpans: HTMLElement[];
          viewContainerRef = component.viewContainerRef;
          rowGroupHeaderContainer = component.ag.rowGroupHeaderContainer;

          init(params: IHeaderParams<any, any>): AgPromise<void> | void {
            let component = params.innerHeaderComponentParams.component as AgGridPivotTableVisualComponent;

            this.headersContainer = document.createElement('div');
            this.headersContainer.classList.add('column-group-headers');

            let groupIndex = params.api.getAllDisplayedColumns().findIndex(x => x === params.column);

            let singleColumn   = pivotColumnDisplay == 'singleColumn';
            let includeHeaders = singleColumn || (groupIndex >= (rowFields.length - 1));

            if (includeHeaders) {
              let path = [];

              for (let f of columnFields) {

                let header = this.viewContainerRef.createComponent(PivotColumnHeader);
                header.setInput('visualField', f);

                header.instance.ellipsisClick.subscribe((e) => {
                  e.stopImmediatePropagation();
                  component.openColumnHeaderMenu(f, e);
                });

                this.headersContainer.appendChild(header.location.nativeElement);

                header.instance.clicked.subscribe( (e) => {
                  e.stopImmediatePropagation();
                  component.cycleColumnHeaderSort(f);
                });

                header.instance.contextMenu.subscribe( (e) => {
                  e.stopImmediatePropagation();
                  component.openColumnHeaderMenu(f, e);
                });
              }
            }
            else {
              for (let f of columnFields) {
                let div = document.createElement('div');
                div.setAttribute('data-testid', `column-group-header-${f.target.referenceString()}`);
                div.classList.add('column-group-header');
                div.classList.add('column-group-header-column');
                div.classList.add('column-group-header-empty');

                let span = document.createElement('span');
                span.classList.add('text');
                div.appendChild(span);

                this.headersContainer.appendChild(div);
              }
            }

            let div = document.createElement('div');
            div.classList.add('column-group-header');
            div.classList.add('column-group-header-row');
            div.classList.add('d-flex');

            if (singleColumn)
              div.classList.add('single-column');

            if (singleColumn) {
              this.rowHeaderTitleSpans = rowFields.map((x, i) => {
                let title = rowFields.map(x => x.shortTitle).join(' > ');

                let span = document.createElement('span');
                span.setAttribute('data-testid', `row-Column-header-${x.target.referenceString()}`);
                span.classList.add('row-level');
                span.title = title;

                effect(() => {
                  span.title = rowFields.map(x => x.shortTitle).join(' > ');
                }, { injector: component.injector });


                if (i > 0) {
                  let icon = document.createElement('span');
                  icon.style.setProperty('--ag-icon-size', '14px');
                  icon.classList.add('ag-icon', 'ag-icon-tree-closed');
                  icon.classList.add('d-inline-block');
                  span.appendChild(icon);
                }

                let text = document.createElement('span');
                text.classList.add('text');
                text.textContent = x.shortTitle;
                span.appendChild(text);

                effect(() => {
                  text.textContent = x.shortTitle;
                }, { injector: component.injector });

                div.appendChild(span);

                return span;
              });
            }
            else {
              let span = document.createElement('span');
              span.classList.add('text');
              span.textContent = params.displayName;
              span.title       = params.displayName;
              div.appendChild(span);

              this.rowHeaderTitleSpans = [span];
            }
            this.rowGroupHeaderContainer.set(div);
            this.headersContainer.appendChild(div);

            // HACK: Cannot replace sort indicator in ag grid alone, so just reposition it
            // next to label
            let header = params.eGridHeader;

            timer(0).pipe(
              takeUntilDestroyed(component.destroyRef),
              tap(() => {
                let sortIndicator = header.querySelector('.ag-sort-indicator-container');
                let rowGroupHeaderContainer = this.rowGroupHeaderContainer();
                if (!!sortIndicator && !!rowGroupHeaderContainer && sortIndicator.parentElement != rowGroupHeaderContainer) {
                  sortIndicator.parentElement.removeChild(sortIndicator);
                  rowGroupHeaderContainer?.appendChild(sortIndicator);
                }
              })
            ).subscribe();
          }

          getGui(): HTMLElement {
            return this.headersContainer;
          }

          refresh(params: IHeaderParams): boolean {
            return true;
          }
        }
      },

      comparator: dataComparer.agGridComparerInferType,
    };
  });

  readonly getRowId: GetRowIdFunc = params => {
    return (params.data as PivotRow).id;
  };

  readonly defaultColDef: ColDef = {
    minWidth: AgGridConstants.MIN_COLUMN_WIDTH,
  };

  readonly columnDefs = computed(() => {
    let pivot = this.component.pivot();

    return untracked(() => {
      let colDefs = [
        ...pivot.rowGroups.map(x => x.columnDef()),
        ...pivot.columnGroups.map(x => x.columnDef()),
        ...pivot.valueGroups.map(x => x.columnDef())
      ];

      return colDefs;
    });
  });

  
  //#region init/reset
  readonly canInit = computed(() => this.component.data.query()?.success() && !this.resetAg());

  reset() {
    if (!this.resetAg()) {
      this._rootNode = null;
      this._expandChildrenOf.clear();
      this.resetAg.set(true);
      this.updateSource();
      this.ag()?.ngOnDestroy();
    }

    this.unsetResetAgDebounced();
  }

  readonly resetAg = signal(false);

  private readonly unsetResetAgDebounced = debounced(() => {
    this.resetAg.set(false);
  }, 200, this.component.destroyRef);
  //#endregion


  //#region source
  readonly source = signal<DataSource>(null);

  updateSource() {
    untracked(() => this.source()?.destroy());
    
    this.source.set(new DataSource(this.component));
  }
  //#endregion
  
  readonly gridReadyState         = signal(false);
  readonly firstDataRenderedState = signal(false);
  readonly gridReady         = computed(() => !!this.ag()?.api && this.gridReadyState());
  readonly firstDataRendered = computed(() => !!this.ag()?.api && this.firstDataRenderedState());

  pendingUISort = false;

  onGridSortChanged(e: SortChangedEvent) {
    let visualFieldSorting = this.component.visual().fieldSorting();

    e.columns?.forEach((c, i) => {
      if (c.getColId() == 'ag-Grid-AutoColumn') {
        this.component.rowFields().forEach(field => {
          applySort(c, field);
        });

        return;
      }

      let group = <PivotGroup>c.getUserProvidedColDef()?.context;
      if (!group) {
        return;
      }

      if (!group.isSortableColumn) {
        return;
      }

     applySort(c, group?.vf)

    });

    //Force ag grid to return to default sorting
    if ((e.source != "api" || this.pendingUISort) && !e.api.getAllGridColumns().some(c => c.getSort())) {
      this.component.reloadPivotData();
      this.pendingUISort = false;
    }

    this.component.broadcastStateChange();

    function applySort(c: Column, field: VisualField) {
      let sortState = c.getSort();
      let sortDirection =
        sortState == 'asc'  ? SortDirection.Ascending  :
        sortState == 'desc' ? SortDirection.Descending : null;

      visualFieldSorting.setSortingOf(
        field,
        sortDirection,
        c.getSortIndex()
      );
    }
  }

  public pendingSortUpdate = false;
   
  onDataLoaded(e: StoreRefreshedEvent) {
    if (this.pendingSortUpdate) {
      let columns = e.api.getAllGridColumns()
          .filter(c => !!c.getSort())
          .map(c => <ColumnState>{ colId: c.getColId(), sort: c.getSort() });

      e.api.applyColumnState({ state: columns.map(c => <ColumnState>{ ...c, sort: null })});

      setTimeout(() => {
        e.api.applyColumnState({
          state: columns
        });
        this.pendingSortUpdate = false;
      });
    }
  }

  onGridColumnMoved(e: ColumnMovedEvent) {
    LOGGER.debug('columnMoved', e);

    if (!e.finished)
      return;

    let visualFields = this.component.roles()?.columns.fields;
    if (!visualFields)
      return;

    if (!e.column)
      return;

    let movedField = (e.column.getUserProvidedColDef().context as PivotGroup)?.vf;
    if (!movedField) {
      LOGGER.warn(`
        Got column moved changed event for column '${e.column.getColId()}', 
        but it does not have a TableField reference assigned.`, e);

      return;
    }

    let order = e.api
      .getAllDisplayedColumns()
      .map(x => (x.getUserProvidedColDef()?.context as PivotGroup)?.vf);
    
    visualFields.sort((a, b) => {
      let ai = order.findIndex(x => x.target.equals(a.target)) ?? 9999;
      let bi = order.findIndex(x => x.target.equals(b.target)) ?? 9999;

      return ai - bi;
    });

    this.component.broadcastStateChange();
  }
  
  onGridCellKeyDown(e: CellKeyDownEvent) {
    let key = e.event as KeyboardEvent;

    if (key.key == ' ') {
      let node = e.api.getDisplayedRowAtIndex(e.rowIndex);
      if (node && !node.leafGroup) {
        node.setExpanded(!node.expanded);
        return;
      }
    }

    if (!key.shiftKey)
      return;

    let arrowKey = key.key.startsWith('Arrow');
    if (!arrowKey) 
      return;

    this.component.startCellNavigation();

    let direction = 
      key.key == 'ArrowUp'   ? -1 :
      key.key == 'ArrowDown' ? +1 : 0;

    if (direction == 0)
      return;

    let row = e.api.getDisplayedRowAtIndex(e.rowIndex + direction);
    if (!row)
      return;

    e.api.setNodesSelected({
      nodes: [row],
      newValue: true,
      source: 'api'
    });
  }

  readonly deepestExpandedLevel = signal(0);

  onRowGroupOpened(e: RowGroupOpenedEvent) {
    this.deepestExpandedLevel.set(
      e.api.getRenderedNodes().reduce((x, r) => Math.max(x, r.level), 0));
  }


  applyFreezeRowHeaders(freeze: boolean) {
    untracked(() => {
      let ag = this.ag();
      if (!ag?.api || ag.api.isDestroyed())
        return;

      let columns = [];

      if (freeze)
        columns = [ROWHEADER_COLID];
        
      this.updateState(state => ({
        ...state,
        columnPinning: {
          leftColIds: columns,
          rightColIds: []
        }
      }), ['columnPinning']);
    });
  }

  private _expandChildrenOf = new Set();

  private readonly _expandChildrenOfLoop = interval(50).pipe(
    takeUntilDestroyed(this.component.destroyRef),
    tap(() => {
      if (!this._expandChildrenOf.size)
        return;

      let handledParents = new Set();
      let nodes: RowNode[] = [];
      let maxCount = 50;
      
      this.forEachNode(n => {
        if (!this._expandChildrenOf.has(n.parent))
          return;

        if (n.stub)
          return;

        handledParents.add(n.parent);
        nodes.push(n as RowNode);
        maxCount--;

        if (maxCount == 0)
          return 'stop';
      });

      handledParents.forEach(p => this._expandChildrenOf.delete(p));

      nodes.forEach(n => {
        if (n.isExpandable()) {
          this._expandChildrenOf.add(n);
          n.setExpanded(true);
        }
      });
    })
  ).subscribe();

  private expandRecursively(node: RowNode) {
    node.setExpanded(true);
    this._expandChildrenOf.add(node);
  }

  private nodeCollapsed(node: RowNode) {
    this._expandChildrenOf.delete(node);
  }

  private allNodesCollapsed() {
    this._expandChildrenOf.clear();
  }


  private _rootNode: RowNode;

  private getRootNode(): RowNode {
    return untracked(() => (this._rootNode ??= this.ag().api.getRowNode(undefined) as any)?.beans?.rowModel?.rootNode);
  }

  private forEachNode(callback: (n: RowNode) => void);
  private forEachNode(callback: (n: RowNode) => 'skip' | 'stop');
  private forEachNode(callback: (n: RowNode) => any) {
    let rootNode = this.getRootNode();
    if (!rootNode) {
      LOGGER.error('No root node!');
      return 'stop';
    }

    return this.forEachNodeChild(rootNode, callback, true);
  }
  
  private forEachNodeChild(n: RowNode, callback: (n: RowNode) => void, recursive: boolean);
  private forEachNodeChild(n: RowNode, callback: (n: RowNode) => 'skip' | 'stop', recursive: boolean);
  private forEachNodeChild(n: RowNode, callback: (n: RowNode) => any, recursive: boolean) {
    let store = n.childStore as any;
    let nodes = store?.cache?.nodeMap?.maps?.get('node');
    if (nodes) {
      for (let c of [...nodes.values()]) {
        let childNode = c.node as RowNode;
        let result = callback(childNode);
        if (result === 'stop')
          return 'stop';

        if (result === 'skip')
          continue;

        if (recursive) {
          if (this.forEachNodeChild(childNode, callback, recursive) === 'stop')
            return 'stop';
        }
      }
    }
  }

  private forEachNodeLeaf(parent: RowNode, callback: (n: RowNode) => void) {
    return this.forEachNodeChild(parent, n => {
      if (!n.isExpandable())
        callback(n);
    }, true);
  }

  readonly getMainMenuItems = (params: GetMainMenuItemsParams, contextMenu?: boolean) => {
    let items: (DefaultMenuItem | MenuItemDef)[] = [];

    let rowClick = 'node' in params && !!params.node;
    let headerClick = !!params.column;
    let component = this.component;
    let agController = this;

    let agApi = this.ag()?.api;

    if (!rowClick && !headerClick)
      return [];

    let unfreezeRows = this.component.state()?.unfreezeRows();

    let freezeRowHeadersItem = {
      name: (unfreezeRows ? 'Freeze Row Headers' : 'Unfreeze Row Headers'),
      icon: '<i class="fas fa-snowflake"></i>',
      disabled: !this.component.rowFields().length,
      action: _ => {
        this.component.state().unfreezeRows.set(!unfreezeRows);
        this.component.broadcastStateChange();
      }
    };
    
    if (rowClick) {
      pushRowHeaderMenuItems();
    }
    else {
      items.push(freezeRowHeadersItem);
      pushHeaderMenuItems();
    }

    return items;

    function pushHeaderMenuItems() {
    let column = params.column;
      if (column) {
        let pivotGroup = column.getUserProvidedColDef()?.context as PivotColumnOrValueGroup;
        let field = pivotGroup?.vf;
        if (field) {
          if (items.length)
            items.push('separator');

          if (field.sortDirection() != SortDirection.Ascending && column.getSort() != 'asc') {
            items.push({
              name: 'Sort Ascending',
              icon: '<i class="fas fa-arrow-up"></i>',
              action: params => {
                sortingAction(column, SortDirection.Ascending)
              }
            });
          }
            
          if (field.sortDirection() != SortDirection.Descending && column.getSort() != 'desc') {
            items.push({
              name: 'Sort Descending',
              icon: '<i class="fas fa-arrow-down"></i>',
              action: params => {
                sortingAction(column, SortDirection.Descending)
              }
            });
          }

          if (field.sorted() || !!column.getSort()) {
           items.push({
              name: 'Clear Sort',
              action: params => {
                clearNonColumnSort();
                component.ag.pendingUISort = true;
                component.broadcastStateChange();
              }
            });
          }
        }

        if (column.getColId() == 'ag-Grid-AutoColumn') {
          if (items.length)
            items.push('separator');

          if (column.getSort() != 'asc') {
            items.push({
              name: 'Sort Ascending',
              icon: '<i class="fas fa-arrow-up"></i>',
              action: params => {
                sortingAction(column, SortDirection.Ascending);
              }
            });
          }

          if (column.getSort() != 'desc') {
            items.push({
              name: 'Sort Descending',
              icon: '<i class="fas fa-arrow-down"></i>',
              action: params => {
                sortingAction(column, SortDirection.Descending);
              }
            });
          }

          if (column.getSort()) {
            items.push({
              name: 'Clear Sort',
              action: params => {
                clearNonColumnSort();
                component.ag.pendingUISort = true;
                component.broadcastStateChange();
              }
            });
          }
        }
      }
      

      if (items.length) 
        items.push('separator');

      if (params.column) { 
        items.push({
          name: 'Autosize This Column',
          action: params => {
            params.api.autoSizeColumns([params.column]);
          }
        });
      }
      
      items.push({
        name: 'Autosize All Columns',
        action: params => {
          params.api.autoSizeAllColumns();
        }
      });
    }

    function pushRowHeaderMenuItems() {
      let node = (params as any).node as RowNode;
      if (node && node.key) {
        let clickedOnRowHeader = (node.key as any).context == (params as any).value.context;
        if (clickedOnRowHeader) {
          if (node?.group) {
            let nodeValue = (node.key as any).context as PivotValue;
            if (nodeValue) {
              if (node.isExpandable()) {
                items.push({
                  name: 'Expand',
                  icon: '<i class="fas fa-chevron-right"></i>',
                  subMenu: [
                    {
                      name: 'Selection',
                      icon: '<i class="far fa-expand"></i>',
                      action: _ => node.setExpanded(true),
                      disabled: node.expanded
                    },
                    {
                      name: 'Entire Level',
                      icon: '<i class="far fa-expand-alt"></i>',
                      action: _ => {
                        agController.expandRecursively(node);
                      }
                    },
                    {
                      name: 'All',
                      icon: '<i class="far fa-layer-plus"></i>',
                      action: ({ api }) => {
                        api.expandAll();
                      }
                    }
                  ]
                });

                items.push({
                  name: 'Collapse',
                  icon: '<i class="fas fa-chevron-right"></i>',
                  subMenu: [
                    {
                      name: 'Selection',
                      icon: '<i class="far fa-expand"></i>',
                      action: ({ api }) => {
                        agController.nodeCollapsed(node);
                        node.setExpanded(false);
                      },
                      disabled: !node.expanded
                    },
                    {
                      name: 'Entire Level',
                      icon: '<i class="far fa-compress-alt"></i>',
                      action: ({ api }) => {
                        let expandedParents = new Set();
                        let minLevel = node.level;

                        agController.forEachNodeLeaf(node, x => {
                          let p = x.parent;
                          while (p && p.level > minLevel && !expandedParents.has(p)) {
                            expandedParents.add(p);
                            agController.nodeCollapsed(p);
                            p.setExpanded(false);
                            p = p.parent;
                          }
                        });

                        agController.nodeCollapsed(node);
                        node.setExpanded(false);
                      },
                    },
                    {
                      name: 'All',
                      icon: '<i class="far fa-layer-minus"></i>',
                      action: ({ api }) => {
                        agController.allNodesCollapsed();
                        api.collapseAll();
                      }
                    }
                  ]
                });
              }
            }
          }
        }
      }

      if (items.length)
        items.push('separator');

      /*
      IN-7425
      items.push('separator');

      if (node?.isSelected()) {
        items.push({
          name: 'Deselect This Row',
          action: _ => node.setSelected(false)
        });
      }
      */

      items.push(freezeRowHeadersItem);
      
      if (items.length)
        items.push('separator');

      function ensureNodesToCopy(callback: Function) {
        let nodesSelected = !!params.api.getSelectedNodes()?.length;
        if (!nodesSelected) {
          params.api.setNodesSelected({
            newValue: true,
            nodes: [node]
          });
        }

        callback();

        if (!nodesSelected) {
          params.api.clearCellSelection();
        }
      }

      items.push({
        name: 'Copy',
        icon: '<i class="far fa-copy"></i>',
        action: _ => ensureNodesToCopy(() => params.api.copyToClipboard())
      });
      
      items.push({
        name: 'Copy with Headers',
        icon: '<i class="far fa-copy"></i>',
        action: _ => ensureNodesToCopy(() => params.api.copyToClipboard({ includeGroupHeaders: true, includeHeaders: true }))
      });

      items.push({
        name: 'Clear Selection',
        icon: '<i class="far fa-ban"></i>',
        action: _ => {
          component.host.selectionClear(VisualHost.EventTrigger.Visual);
        }
      });
    }

    function sortingAction(column: Column<any>, sortDirection: SortDirection) {
      //Reset all except pivot columns
      clearNonColumnSort()

      agApi.applyColumnState({ state: [{ colId: column.getColId(), sort: sortDirection == SortDirection.Ascending ? 'asc' : 'desc'}] });
      component.ag.pendingUISort = true;

      component.broadcastStateChange();
    }

    function clearNonColumnSort() {
      let columns = agApi.getPivotColumns().map(c => (<ColumnState>{ colId: c.getColId() }));

      agApi.applyColumnState({
        state: columns,
        defaultState: { sort: null, sortIndex: null },
      });
    }
  };
  
  readonly getContextMenuItems = (params: GetContextMenuItemsParams) => {
    return this.getMainMenuItems(<any>params, true);
  };
  
  readonly getNoItems = (params: any) => [];

  applyColumnSizingState() {
    let columns = this.component.state()?.columns();
    this.updateState((state, api) => {
      columns ??= [];
      let columnSizingModel = [...(state.columnSizing?.columnSizingModel ?? [])];
      let max = Math.min(columns.length, columnSizingModel.length);
      let changed = false;

      for (let i = 0; i < max; ++i) {
        let model = columnSizingModel[i];
        let fieldRef = (api.getColumnDef(model.colId).context as PivotGroup)?.vf?.referenceString();

        let column = columns.find(c => c.field == fieldRef || c.field == model.colId);
        if (column) {
          if (model.width == column.width && model.flex == column.flex) {
            continue;
          }

          columnSizingModel[i] = {
            ...model,
            ...column
          };

          changed = true;
        }
      }

      if (!changed)
        return state;

      return {
        ...state,

        columnSizing: {
          ...state.columnSizing,
          columnSizingModel
        }
      };
    }, ['columnSizing']);
  }

  applyScrollState() {
    let scroll = this.component.state()?.scroll();
    this.updateState(state => ({
      ...state,

      scroll: {
        ...state.scroll,
        ...scroll
      },
    }), ['scroll']);
  }

  private updateState(update: (x: GridState, api?: GridApi) => GridState, propsToInclude: GridStateKey[]) {
    let grid = this.component.agGrid();
    if (grid?.api && !grid.api.isDestroyed()) {
      let prevState = grid.api.getState() ?? {};
      let state = update(prevState, grid.api);
      if (prevState !== state) {
        let excludedProps: GridStateKey[] = ['rowGroupExpansion', 'ssrmRowGroupExpansion'];
        grid.api.setState(state, excludedProps);
      }
    }
  }
}
